import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { HttpParams } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { ApiCallState, DataList, DataObject, RequestState, SubscriptionPlans } from '@app/_shared/interfaces';
import { StripeSubscription } from '@app/_shared/interfaces/http/external-subscripition';
import { PostingCredits } from '@app/_shared/interfaces/http/posting-credits';
import { SubscriptionPlansService } from '@app/_shared/service';
import { ExternalSubscriptionsService } from '@app/_shared/service/http/external-subscriptions.service';
import { PostingCreditsService } from '@app/_shared/service/http/posting-credits.service';
import { SnackBarService } from '@app/_shared/snack-bars/snack-bar.service';
import { TranslocoService } from '@jsverse/transloco';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

export interface SubscriptionState {
  postingCredits: DataObject<PostingCredits> | null;
  externalSubscription: DataObject<StripeSubscription> | null;
  subscriptionPlans: DataList<SubscriptionPlans, any>;
  requestState: {
    externalSubscription: ApiCallState;
    postingCredits: ApiCallState;
    subscriptionPlans: ApiCallState;
  };
}

const initialState: SubscriptionState = {
  postingCredits: null,
  externalSubscription: null,
  subscriptionPlans: { data: [] },
  requestState: {
    externalSubscription: RequestState.INIT,
    postingCredits: RequestState.INIT,
    subscriptionPlans: RequestState.INIT,
  },
};

export const SubscriptionsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('SubscriptionsStore'),
  withState(initialState),
  withComputed(({ requestState }) => ({
    isLoadingExternalSubscription: computed(() => requestState().externalSubscription === RequestState.LOADING),
    isLoadingSubscriptionPlans: computed(() => requestState().subscriptionPlans === RequestState.LOADING),
    hasPostingCreditsError: computed(() => requestState().postingCredits === RequestState.ERROR),
    hasSubscriptionPlansError: computed(() => requestState().subscriptionPlans === RequestState.ERROR),
  })),
  withMethods(
    (
      store,
      postingCreditsService = inject(PostingCreditsService),
      externalSubscriptionsService = inject(ExternalSubscriptionsService),
      subscriptionPlansService = inject(SubscriptionPlansService),
      snackBarService = inject(SnackBarService),
      translocoService = inject(TranslocoService),
    ) => ({
      getPostingCredits: rxMethod<number>(
        pipe(
          switchMap((storeId) => {
            updateState(store, 'Loading Posting Credits', (state) => ({
              requestState: {
                ...state.requestState,
                postingCredits: RequestState.LOADING,
              },
            }));

            return postingCreditsService.index(new HttpParams().set('filter[store_id]', storeId)).pipe(
              tapResponse({
                next: (response) => {
                  if (response && response.data) {
                    updateState(store, 'Posting Credits Loaded', {
                      postingCredits: response.data,
                      requestState: {
                        ...store.requestState(),
                        postingCredits: RequestState.INIT,
                      },
                    });
                  } else {
                    updateState(store, 'No Posting Credits Found', {
                      postingCredits: null,
                      requestState: {
                        ...store.requestState(),
                        postingCredits: RequestState.INIT,
                      },
                    });
                  }
                },
                error: (error: any) => {
                  updateState(store, 'Posting Credits Loading Error', (state) => ({
                    postingCredits: null,
                    requestState: {
                      ...state.requestState,
                      postingCredits: RequestState.ERROR,
                    },
                  }));
                },
              }),
            );
          }),
        ),
      ),

      getExternalSubscription: rxMethod<number>(
        pipe(
          switchMap((storeId) => {
            updateState(store, 'Loading External Subscription', (state) => ({
              externalSubscription: null,
              requestState: {
                ...state.requestState,
                externalSubscription: RequestState.LOADING,
              },
            }));

            return externalSubscriptionsService.index(new HttpParams().set('filter[store_id]', storeId)).pipe(
              tapResponse({
                next: ({ data }) => {
                  if (data && data.length > 0) {
                    updateState(store, 'External Subscription Loaded', {
                      externalSubscription: data[0],
                      requestState: {
                        ...store.requestState(),
                        externalSubscription: RequestState.INIT,
                      },
                    });
                  } else {
                    updateState(store, 'External Subscription Empty', (state) => ({
                      requestState: { ...state.requestState, externalSubscription: RequestState.INIT },
                    }));
                  }
                },
                error: (error: any) => {
                  snackBarService.openFailure({
                    body: translocoService.translate('general.messages.unexpected_error'),
                  });
                  updateState(store, 'External Subscription Loading Error', (state) => ({
                    requestState: {
                      ...state.requestState,
                      externalSubscription: RequestState.ERROR,
                    },
                  }));
                },
              }),
            );
          }),
        ),
      ),

      getSubscriptionPlans: rxMethod<string>(
        pipe(
          switchMap((billingDriver) => {
            updateState(store, 'Loading Subscription Plans', (state) => ({
              subscriptionPlans: { data: [] },
              requestState: {
                ...state.requestState,
                subscriptionPlans: RequestState.LOADING,
              },
            }));

            return subscriptionPlansService.index(new HttpParams().set('filter[billing_driver]', billingDriver)).pipe(
              tapResponse({
                next: (response) => {
                  updateState(store, 'Subscription Plans Loaded', {
                    subscriptionPlans: {
                      ...response,
                      data: [...response.data].sort(
                        (a, b) => a.attributes.acquirer_limit - b.attributes.acquirer_limit,
                      ),
                    },
                    requestState: {
                      ...store.requestState(),
                      subscriptionPlans: RequestState.INIT,
                    },
                  });
                },
                error: (error: any) => {
                  updateState(store, 'Subscription Plans Loading Error', (state) => ({
                    requestState: {
                      ...state.requestState,
                      subscriptionPlans: RequestState.ERROR,
                    },
                  }));
                },
              }),
            );
          }),
        ),
      ),

      // Manual state update method for testing/debugging
      updateState: (updates: Partial<SubscriptionState>) => {
        updateState(store, 'Manual State Update', (state) => ({
          ...state,
          ...updates,
        }));
      },
    }),
  ),
);
